import type { KyInstance } from 'ky';

import { wolfswapFactionsApi } from '@/lib/api/clients';

import {
    AGENT_REACTIVATE_TIMEOUT_MS,
    ENSURE_TRIGGERS_TIMEOUT_MS,
    LIMIT_ORDER_TIMEOUT_MS,
    UPDATE_TRIGGERS_TIMEOUT_MS,
    UPDATE_MARGIN_TIMEOUT_MS,
} from './constants';
import { throwIfFundAuthorizationChallenge } from './fundAuthorizationChallenge';
import {
    agentActivateHip3ResponseSchema,
    agentBuilderFeeResponseSchema,
    agentReactivateResponseSchema,
    agentStatusResponseSchema,
    balanceResponseSchema,
    balanceLightResponseSchema,
    bridgeQuoteResponseSchema,
    collateralResponseSchema,
    depositStartResponseSchema,
    ensureTriggersResponseSchema,
    marketDetailResponseSchema,
    positionResponseSchema,
    positionsResponseSchema,
    quoteResponseSchema,
    speedJobResponseSchema,
    speedJobsResponseSchema,
    bridgeCatalogSchema,
    speedConfigSchema,
    speedGasMetricsSchema,
    speedMarketsResponseSchema,
    limitCancelResponseSchema,
    limitExecuteResponseSchema,
    limitOpenResponseSchema,
    tradeCloseResponseSchema,
    tradeOpenResponseSchema,
    updateTriggersResponseSchema,
    updateMarginResponseSchema,
    userTradesResponseSchema,
    walletResponseSchema,
    withdrawStartResponseSchema,
    liveTradesResponseSchema,
    liqMapResponseSchema,
    migrateQuoteResponseSchema,
    migrateStartResponseSchema,
    exposureResponseSchema,
    cancelUntrackedOrderResponseSchema,
} from './schemas';
import type {
    BridgeCatalog,
    AgentActivateHip3Response,
    FundAuthorizationsWire,
    AgentReactivateResponse,
    AgentStatusResponse,
    BalanceResponse,
    BalanceLightResponse,
    BridgeQuoteResponse,
    CollateralResponse,
    DepositQuoteRequest,
    EnsureTriggersResponse,
    FetchTradesParams,
    MarketDetailResponse,
    PositionResponse,
    PositionsResponse,
    QuoteResponse,
    LimitCancelRequest,
    LimitCancelResponse,
    LimitExecuteRequest,
    LimitExecuteResponse,
    LimitOpenRequest,
    LimitOpenResponse,
    MigrateQuote,
    SpeedJobResponse,
    SpeedJobsResponse,
    SpeedConfig,
    SpeedGasMetrics,
    SpeedMarketsResponse,
    StartDepositRequest,
    StartMigrateRequest,
    StartWithdrawRequest,
    TradeCloseResponse,
    TradeCloseRequest,
    TradeOpenResponse,
    TradeOpenRequest,
    UpdateTriggersRequest,
    UpdateTriggersResponse,
    UpdateMarginRequest,
    UpdateMarginResponse,
    UserTrade,
    WalletResponse,
    WithdrawQuoteRequest,
    LiveTradesResponse,
    LiqMapResponse,
    CancelUntrackedOrderRequest,
    CancelUntrackedOrderResponse,
    FetchExposureParams,
    UntrackedExposure,
} from './types';

/**
 * Service functions for the Hyperliquid speed-trading API.
 *
 * Public reads (`config`, `markets`, `collateral`) take the unauthenticated
 * `wolfswapApi` client; everything else requires the authenticated client
 * (`createWolfswapAuthedApi`) which injects the JWT bearer token. All `/speed`
 * routes operate on HyperEVM (networkId 999); the backend rejects other ids.
 */

// --- public reads ---

export async function fetchSpeedConfig(api: KyInstance): Promise<SpeedConfig> {
    const data = await api.get('speed/config').json();
    return speedConfigSchema.parse(data);
}

/**
 * Reads the backend's bridge registry (`GET /speed/bridges` on MoonHub.API):
 * which bridges each chain uses, in failover order, and the legs each can
 * carry. Public (no auth). Populates the bridge testing switch.
 */
export async function fetchBridgeCatalog(
    api: KyInstance,
    signal?: AbortSignal,
): Promise<BridgeCatalog> {
    const data = await api.get('speed/bridges', { signal }).json();
    return bridgeCatalogSchema.parse(data);
}

/**
 * Reads HyperEVM chain-load telemetry (`GET /speed/gas`) from the factions
 * `speed` facet. Returns `{ enabled: false }` when the feature is off; callers
 * must treat that (and any error/404) as "render nothing". Public (no auth).
 */
export async function fetchSpeedGasMetrics(
    api: KyInstance,
    signal?: AbortSignal,
): Promise<SpeedGasMetrics> {
    const data = await api.get('speed/gas', { signal }).json();
    return speedGasMetricsSchema.parse(data);
}

export async function fetchMarkets(
    api: KyInstance,
): Promise<SpeedMarketsResponse> {
    const data = await api.get('speed/markets').json();
    return speedMarketsResponseSchema.parse(data);
}

export async function fetchMarketDetail(
    api: KyInstance,
    coin: string,
): Promise<MarketDetailResponse> {
    const data = await api
        .get(`speed/markets/${encodeURIComponent(coin)}`)
        .json();
    return marketDetailResponseSchema.parse(data);
}

export async function fetchCollateral(
    api: KyInstance,
    coin: string,
    leverage: number,
): Promise<CollateralResponse> {
    const data = await api
        .get(
            `speed/markets/${encodeURIComponent(coin)}/${encodeURIComponent(
                leverage.toString(),
            )}`,
        )
        .json();
    return collateralResponseSchema.parse(data);
}

// --- authenticated reads ---

export async function fetchPosition(
    authedApi: KyInstance,
    coin: string,
    signal?: AbortSignal,
): Promise<PositionResponse> {
    const data = await authedApi
        .get(`speed/position/${encodeURIComponent(coin)}`, { signal })
        .json();
    return positionResponseSchema.parse(data);
}

/**
 * Every open position, in one request.
 *
 * Used for the markets the client is NOT looking at: `useLivePosition` polls the
 * charted market on its own hot cadence, while this covers the rest at a slower
 * one so a position going bad off-screen is still visible in the table.
 */
export async function fetchPositions(
    authedApi: KyInstance,
    signal?: AbortSignal,
): Promise<PositionsResponse> {
    const data = await authedApi.get('speed/positions', { signal }).json();
    return positionsResponseSchema.parse(data);
}

export async function fetchBalance(
    authedApi: KyInstance,
    signal?: AbortSignal,
): Promise<BalanceResponse> {
    const data = await authedApi.get('speed/balance', { signal }).json();
    return balanceResponseSchema.parse(data);
}

/**
 * The parts of a balance the chain cannot report — addresses, the unified flag, and the
 * withdraw policy inputs. Pair with an on-chain read of the USDC buckets (see
 * `features/wallet/services/onchainBalance.ts`) so the backend does no RPC work per refresh.
 */
export async function fetchBalanceLight(
    authedApi: KyInstance,
    signal?: AbortSignal,
): Promise<BalanceLightResponse> {
    const data = await authedApi.get('speed/balance/light', { signal }).json();
    return balanceLightResponseSchema.parse(data);
}

export async function fetchWallet(
    authedApi: KyInstance,
    signal?: AbortSignal,
): Promise<WalletResponse> {
    const data = await authedApi.get('speed/wallet', { signal }).json();
    return walletResponseSchema.parse(data);
}

/**
 * Reads the user's Hyperliquid agent-key state (`GET /speed/agent/status`). Pure
 * db read — `needsReactivation` is true when the approval is missing, expired, or
 * within the refresh margin. Authenticated (MoonHub.API).
 */
export async function fetchAgentStatus(
    authedApi: KyInstance,
    signal?: AbortSignal,
): Promise<AgentStatusResponse> {
    const data = await authedApi.get('speed/agent/status', { signal }).json();
    return agentStatusResponseSchema.parse(data);
}

/**
 * Proactively re-approves the user's Hyperliquid agent
 * (`POST /speed/agent/reactivate`) so the next open/close does not stall on an
 * inline re-approval. Long-running (service-wallet tx + `extraAgents`
 * confirmation) — the default 30s client timeout is overridden.
 */
export async function reactivateAgent(
    authedApi: KyInstance,
    fundAuthorizations: FundAuthorizationsWire = {},
): Promise<AgentReactivateResponse> {
    const data = await authedApi
        .post('speed/agent/reactivate', {
            // the HyperCore bootstrap this can trigger signs an approve_erc20 and an hl_fund, both
            // fund operations, so the body carries the fund-authorization map (empty on the first
            // attempt — that is the strict-handshake opt-in that makes the backend answer with
            // descriptors instead of an opaque failure).
            json: { fundAuthorizations },
            timeout: AGENT_REACTIVATE_TIMEOUT_MS,
        })
        .json();
    // the HyperCore bootstrap answers the strict handshake with the challenge
    // (HTTP 200, no job) exactly like startDeposit/startWithdraw — see the
    // `fundAuthorizationRequiredResponse` call sites in
    // MoonHub.API/managers/SpeedTradingJobManager.ts (startAgentSetup and its
    // claimed-setup preparation both reach it). Without this translation the
    // challenge body was parsed as a job and the caller saw a raw zod dump
    // instead of the signing ceremony.
    throwIfFundAuthorizationChallenge(data);
    return agentReactivateResponseSchema.parse(data);
}

/**
 * Re-approves the builder code for a wallet whose agent is already live
 * (`POST /speed/agent/builder-fee`). No user signature: RustSigner attests from
 * the live session and a service wallet submits. Called silently whenever the
 * status reports `builderApprovalStale`; the backend bounds the retries.
 */
export async function approveBuilderFee(
    authedApi: KyInstance,
): Promise<{ approved: boolean; decibps: number | null }> {
    const data = await authedApi
        .post('speed/agent/builder-fee', {
            timeout: AGENT_REACTIVATE_TIMEOUT_MS,
        })
        .json();
    return agentBuilderFeeResponseSchema.parse(data);
}

/** Why the client believes the on-chain agent registration is gone. */
export type AgentPrunedReason = 'fullWithdrawal' | 'agentMissing';

/**
 * Tells the backend its agent bookkeeping is stale (`POST /speed/agent/report-pruned`).
 *
 * Hyperliquid PRUNES an account's agent registration and builder approval when the account is
 * emptied completely, and `GET /speed/agent/status` is a pure db read that cannot see that. Two
 * client-side signals stand in for it: a full EOA-direct withdrawal that drained HyperCore
 * (`fullWithdrawal`) and an `extraAgents` read that no longer lists our agent (`agentMissing`).
 * The endpoint is idempotent and answers with the refreshed agent status, so the caller can seed
 * or invalidate the same query the banner reads.
 */
export async function reportAgentPruned(
    authedApi: KyInstance,
    reason: AgentPrunedReason,
): Promise<AgentStatusResponse> {
    const data = await authedApi
        .post('speed/agent/report-pruned', { json: { reason } })
        .json();
    return agentStatusResponseSchema.parse(data);
}

/**
 * Runs the one-time HIP-3 (unified-account) activation for the user's existing
 * adapter (`POST /speed/agent/activate-hip3`). Required once before the first
 * builder-market open; the backend signs, posts and confirm-polls the
 * abstraction switch with the already-approved agent. Long-running — the
 * default 30s client timeout is overridden. Authed WOLFSWAP client
 * (MoonHub.API, cookie-bearing).
 *
 * No fund-authorization handshake here: `/speed/agent/activate-hip3` is served
 * by `HyperliquidTradeManager.activateHip3`, which moves no funds and never
 * builds a `FundAuthorizationBundle`. The three
 * `fundAuthorizationRequiredResponse(` call sites in MoonHub.API all live in
 * `SpeedTradingJobManager` (deposit, withdraw, agent setup), so those are the
 * only three services that translate the challenge.
 */
export async function activateHip3(
    authedApi: KyInstance,
): Promise<AgentActivateHip3Response> {
    const data = await authedApi
        .post('speed/agent/activate-hip3', {
            // same empty-json-body note as reactivateAgent above.
            json: {},
            timeout: AGENT_REACTIVATE_TIMEOUT_MS,
        })
        .json();
    return agentActivateHip3ResponseSchema.parse(data);
}

/**
 * Installs any missing native TP/SL trigger orders for the user's OPEN trades
 * (`POST /speed/trade/ensure-triggers`). Used by the ambiguous-transport recovery
 * path: when an open timed out on the FE but Hyperliquid accepted it and factions
 * later flipped the trade to Open, the requested TP/SL legs were never placed.
 * Authed WOLFSWAP client (MoonHub.API, cookie-bearing) — it agent-signs, so it
 * cannot be served off the factions read facet. Idempotent; installs only missing
 * requested legs. Long-running (signs + posts `/exchange` per leg), so the default
 * 30s client timeout is overridden.
 */
export async function ensureTriggers(
    authedApi: KyInstance,
): Promise<EnsureTriggersResponse> {
    const data = await authedApi
        .post('speed/trade/ensure-triggers', {
            // no body (auth + cookie only); send an empty object so the client's default
            // Content-Type: application/json has valid json to parse. see reactivateAgent above.
            json: {},
            timeout: ENSURE_TRIGGERS_TIMEOUT_MS,
        })
        .json();
    return ensureTriggersResponseSchema.parse(data);
}

export async function fetchUserTrades(
    authedApi: KyInstance,
    params: FetchTradesParams = {},
): Promise<UserTrade[]> {
    const searchParams: Record<string, string | number> = {};
    if (params.status) {
        searchParams.status = params.status;
    }
    if (params.limit !== undefined) {
        searchParams.limit = params.limit;
    }
    if (params.page !== undefined) {
        searchParams.page = params.page;
    }

    const data = await authedApi
        .get('speed/trades', { searchParams, signal: params.signal })
        .json();
    return userTradesResponseSchema.parse(data).trades;
}

// --- trade actions (authenticated, gasless) ---

/**
 * The public live tape's seed page. Unauthenticated on purpose — it carries no
 * per-user content, so it takes the plain factions client rather than the JWT
 * one, and a logged-out visitor sees the same feed as everyone else.
 *
 * Paged by an `_id` cursor (`before`), never by page number: the tape grows at
 * the head, so numbered pages would skip and repeat rows as trades arrive.
 *
 * `trader` is the only filter the server serves, and the only one that needs it:
 * a single trader's rows among the last few hundred global trades is almost
 * always none, so it has to reach past the window the page holds. Status,
 * direction, coin, size and pnl are filtered client-side over the loaded rows —
 * there is deliberately no parameter for them, because a parameter the endpoint
 * ignores reads as a working filter. (There was one: `coin`, sent for months and
 * never read.)
 */
export async function fetchLiveTrades(
    params: {
        limit?: number;
        before?: number | null;
        trader?: string | null;
        signal?: AbortSignal;
    } = {},
): Promise<LiveTradesResponse> {
    const searchParams: Record<string, string | number> = {};
    if (params.limit !== undefined) searchParams.limit = params.limit;
    if (params.before !== undefined && params.before !== null)
        searchParams.before = params.before;
    if (params.trader) searchParams.trader = params.trader;

    const data = await wolfswapFactionsApi
        .get('speed/live', { searchParams, signal: params.signal })
        .json();
    return liveTradesResponseSchema.parse(data);
}

/**
 * The public liquidation map for one market: where the sampled market-wide
 * leveraged money gets force-closed. Unauthenticated, same client as the tape.
 * The server answers in the exchange's spelling whatever case is sent.
 */
export async function fetchLiqMap(
    coin: string,
    signal?: AbortSignal,
): Promise<LiqMapResponse> {
    const data = await wolfswapFactionsApi
        .get(`speed/liqmap/${encodeURIComponent(coin)}`, { signal })
        .json();
    return liqMapResponseSchema.parse(data);
}

export async function quoteOpen(
    authedApi: KyInstance,
    body: TradeOpenRequest,
): Promise<QuoteResponse> {
    const data = await authedApi.post('speed/quote', { json: body }).json();
    return quoteResponseSchema.parse(data);
}

export async function openTrade(
    authedApi: KyInstance,
    body: TradeOpenRequest,
): Promise<TradeOpenResponse> {
    const data = await authedApi
        .post('speed/trade/open', { json: body })
        .json();
    return tradeOpenResponseSchema.parse(data);
}

export async function closeTrade(
    authedApi: KyInstance,
    body: TradeCloseRequest,
): Promise<TradeCloseResponse> {
    const data = await authedApi
        .post('speed/trade/close', { json: body })
        .json();
    return tradeCloseResponseSchema.parse(data);
}

/**
 * Places a GTC limit entry (`POST /speed/trade/limit/open`).
 *
 * Authed WOLFSWAP client (MoonHub.API): it agent-signs the order, so it cannot
 * be served off the factions read facet. The response says whether the order is
 * `Resting` on the book or already `Open` because the price crossed it.
 */
export async function openLimitOrder(
    authedApi: KyInstance,
    body: LimitOpenRequest,
): Promise<LimitOpenResponse> {
    const data = await authedApi
        .post('speed/trade/limit/open', { json: body })
        .json();
    return limitOpenResponseSchema.parse(data);
}

/**
 * Cancels the coin's resting limit order (`POST /speed/trade/limit/cancel`).
 *
 * A `status: 'Open'` answer is NOT an error: the order filled before the cancel
 * reached Hyperliquid, so the row is a running position and the caller must
 * show it as one. Long-running (a cancel plus an authoritative position read),
 * so the default 30s client timeout is overridden.
 */
export async function cancelLimitOrder(
    authedApi: KyInstance,
    body: LimitCancelRequest,
): Promise<LimitCancelResponse> {
    const data = await authedApi
        .post('speed/trade/limit/cancel', {
            json: body,
            timeout: LIMIT_ORDER_TIMEOUT_MS,
        })
        .json();
    return limitCancelResponseSchema.parse(data);
}

/**
 * "Fill now" (`POST /speed/trade/limit/execute`): cancels the resting order and
 * opens the same size at market. `filledBeforeExecute` means the resting order
 * had already filled, so nothing new was opened — the existing position stands.
 */
export async function executeLimitOrder(
    authedApi: KyInstance,
    body: LimitExecuteRequest,
): Promise<LimitExecuteResponse> {
    const data = await authedApi
        .post('speed/trade/limit/execute', {
            json: body,
            timeout: LIMIT_ORDER_TIMEOUT_MS,
        })
        .json();
    return limitExecuteResponseSchema.parse(data);
}

/**
 * Edits the TP/SL of the coin's already-open trade
 * (`POST /speed/trade/update-triggers`). Tri-state per leg — see
 * {@link UpdateTriggersRequest}: absent leaves the leg alone, `null` cancels it,
 * a price string (re)places it. Authed WOLFSWAP client (MoonHub.API): it
 * agent-signs the cancel/place, so it cannot be served off the factions read
 * facet. Long-running (up to two `/exchange` round-trips), so the default 30s
 * client timeout is overridden. Rate-limited 6/min server-side — the caller must
 * not fire it on every keystroke.
 */
export async function updateTriggers(
    authedApi: KyInstance,
    body: UpdateTriggersRequest,
): Promise<UpdateTriggersResponse> {
    const data = await authedApi
        .post('speed/trade/update-triggers', {
            json: body,
            timeout: UPDATE_TRIGGERS_TIMEOUT_MS,
        })
        .json();
    return updateTriggersResponseSchema.parse(data);
}

/**
 * Adds margin to the user's open isolated position on `coin`
 * (`POST /speed/trade/update-margin`). Authed WOLFSWAP client (MoonHub.API):
 * it agent-signs Hyperliquid's `updateIsolatedMargin`, so it cannot be served
 * off the factions read facet. The backend refuses an amount above the free
 * HyperCore margin with a plain message naming what IS available. Rate-limited
 * 6/min server-side — the caller must not fire it on every keystroke.
 */
export async function updateMargin(
    authedApi: KyInstance,
    body: UpdateMarginRequest,
): Promise<UpdateMarginResponse> {
    const data = await authedApi
        .post('speed/trade/update-margin', {
            json: body,
            timeout: UPDATE_MARGIN_TIMEOUT_MS,
            retry: 0,
        })
        .json();
    return updateMarginResponseSchema.parse(data);
}

// --- deposit saga (authenticated) ---

/**
 * Display pre-quotes: what this exact amount would cost and yield, priced by
 * the bridge the backend would actually pick, BEFORE the user commits to it.
 *
 * Read-only and authorization-free by design. Nothing is reserved, nothing is
 * signed, and the figures are never carried into execution: prepare re-quotes
 * and the fund-authorization challenge states what is really being signed for.
 * They exist so the button does not unlock over a number nobody has checked.
 *
 * `signal` is required in practice: the caller re-quotes on every amount
 * change, so a stale answer must be abortable or it will overwrite a newer one.
 */
export async function fetchWithdrawQuote(
    authedApi: KyInstance,
    body: WithdrawQuoteRequest,
    signal?: AbortSignal,
): Promise<BridgeQuoteResponse> {
    const data = await authedApi
        .post('speed/withdraw/quote', { json: body, signal })
        .json();
    return bridgeQuoteResponseSchema.parse(data);
}

export async function fetchDepositQuote(
    authedApi: KyInstance,
    body: DepositQuoteRequest,
    signal?: AbortSignal,
): Promise<BridgeQuoteResponse> {
    const data = await authedApi
        .post('speed/deposit/quote', { json: body, signal })
        .json();
    return bridgeQuoteResponseSchema.parse(data);
}

export async function startDeposit(
    authedApi: KyInstance,
    body: StartDepositRequest,
): Promise<SpeedJobResponse> {
    const data = await authedApi.post('speed/deposit', { json: body }).json();
    // a strict request that still needs user signatures is answered with the
    // fund-authorization challenge (HTTP 200, no job) instead of a job body.
    throwIfFundAuthorizationChallenge(data);
    return depositStartResponseSchema.parse(data);
}

export async function fetchSpeedJob(
    authedApi: KyInstance,
    jobId: string,
    signal?: AbortSignal,
): Promise<SpeedJobResponse> {
    const data = await authedApi
        .get(`speed/job/${encodeURIComponent(jobId)}`, { signal })
        .json();
    return speedJobResponseSchema.parse(data);
}

export async function fetchSpeedJobs(
    authedApi: KyInstance,
    limit = 10,
    signal?: AbortSignal,
): Promise<SpeedJobsResponse> {
    const data = await authedApi
        .get('speed/jobs', { searchParams: { limit }, signal })
        .json();
    return speedJobsResponseSchema.parse(data);
}

export async function recordSpeedJobTxHashes(
    authedApi: KyInstance,
    jobId: string,
    txHashes: string[],
): Promise<SpeedJobResponse> {
    const data = await authedApi
        .post(`speed/job/${encodeURIComponent(jobId)}/tx-hashes`, {
            json: { txHashes },
        })
        .json();
    return speedJobResponseSchema.parse(data);
}

/**
 * Tells the backend which bridge intent the user's wallet just registered on
 * chain (`POST /speed/job/:id/bridge-intent`), resuming a withdrawal that was
 * parked at `status: 'actionRequired'` / `step: 'awaitingBridgeIntent'` because
 * its bridge hop had to be re-routed.
 *
 * Sensitive (it releases the hop's funds), so it goes to MoonHub.API on the
 * authed client, and answers with the job in its resumed state. The backend
 * reads the registry itself: this call names the entry, it does not prove it.
 */
export async function registerBridgeIntent(
    authedApi: KyInstance,
    jobId: string,
    bridgeIntent: { id: string; txHash: string },
): Promise<SpeedJobResponse> {
    const data = await authedApi
        .post(`speed/job/${encodeURIComponent(jobId)}/bridge-intent`, {
            json: bridgeIntent,
        })
        .json();
    return speedJobResponseSchema.parse(data);
}

export async function recheckSpeedJob(
    authedApi: KyInstance,
    jobId: string,
): Promise<SpeedJobResponse> {
    const data = await authedApi
        .post(`speed/job/${encodeURIComponent(jobId)}/recheck`)
        .json();
    return speedJobResponseSchema.parse(data);
}

/**
 * Cancels a deposit still waiting on the user's wallet signature. The backend
 * refuses (`depositInFlight`) once a transaction hash is recorded or the bridge
 * has seen the leg, so a deposit that already left the wallet runs on.
 */
export async function cancelSpeedJob(
    authedApi: KyInstance,
    jobId: string,
): Promise<SpeedJobResponse> {
    const data = await authedApi
        .post(`speed/job/${encodeURIComponent(jobId)}/cancel`)
        .json();
    return speedJobResponseSchema.parse(data);
}

/**
 * Resumes an unsigned durable job after the user establishes fresh trading
 * authority. MoonHub confirms that no irreversible action occurred before
 * accepting this request.
 */
export async function reauthorizeSpeedJob(
    authedApi: KyInstance,
    jobId: string,
): Promise<SpeedJobResponse> {
    const data = await authedApi
        .post(`speed/job/${encodeURIComponent(jobId)}/reauthorize`, {
            json: {},
        })
        .json();
    return speedJobResponseSchema.parse(data);
}
// --- withdraw saga (authenticated) ---

export async function startWithdraw(
    authedApi: KyInstance,
    body: StartWithdrawRequest,
): Promise<SpeedJobResponse> {
    const data = await authedApi.post('speed/withdraw', { json: body }).json();
    // same challenge-over-200 contract as startDeposit.
    throwIfFundAuthorizationChallenge(data);
    return withdrawStartResponseSchema.parse(data);
}

// --- V1 to V2 migration (authenticated) ---

/**
 * `POST /speed/migrate`: moves the V1 custodial balance out of HyperCore and
 * pays it to the user's Snappy Wallet on HyperEVM. Sensitive, so it goes to
 * MoonHub.API on the authed client, with the same challenge-over-200 contract
 * as {@link startWithdraw}.
 */
export async function startMigrate(
    authedApi: KyInstance,
    body: StartMigrateRequest,
): Promise<SpeedJobResponse> {
    const data = await authedApi.post('speed/migrate', { json: body }).json();
    throwIfFundAuthorizationChallenge(data);
    return migrateStartResponseSchema.parse(data);
}

/**
 * `GET /speed/migrate/quote`: what a migration started now would move (USD),
 * HyperCore reserve already left out. Read off MoonHub.API on the authed
 * client, like the start call; it is the figure the Migrate button and the
 * dialog show, never `withdrawableUsdc`.
 */
export async function fetchMigrateQuote(
    authedApi: KyInstance,
    signal?: AbortSignal,
): Promise<MigrateQuote> {
    const data = await authedApi.get('speed/migrate/quote', { signal }).json();
    return migrateQuoteResponseSchema.parse(data);
}

// --- untracked exposure (what the exchange holds that no row explains) ---

/**
 * `GET speed/exposure` — the positions and resting orders on Hyperliquid that
 * none of the user's trade rows account for.
 *
 * Read-only, so it is served by the factions speed facet and takes the FACTIONS
 * authed client, exactly like `fetchUserTrades`. The envelope is always 200: a
 * read that could not reach the exchange comes back `{ success: false, error }`
 * and this returns `null`, because a panel that cannot see the exchange must
 * show nothing rather than an empty "all clear".
 */
export async function fetchExposure(
    factionsApi: KyInstance,
    params: FetchExposureParams,
): Promise<UntrackedExposure | null> {
    const data = await factionsApi
        .get('speed/exposure', {
            searchParams: { networkId: params.networkId },
            signal: params.signal,
        })
        .json();
    const parsed = exposureResponseSchema.parse(data);
    return parsed.success ? parsed.exposure : null;
}

/**
 * `POST speed/trade/orders/cancel` — drop ONE untracked resting order by its
 * Hyperliquid oid.
 *
 * Authed WOLFSWAP client (MoonHub.API): it agent-signs the cancel, so it cannot
 * be served off the factions read facet. Errors arrive as ordinary
 * `/speed/trade/*` ValidationErrors (`orderNotFound`, `orderTracked`,
 * `rateLimited`) and belong in the shared error-to-toast mapping, never on
 * screen verbatim. An untracked POSITION is closed with the existing
 * {@link closeTrade}, which sizes from the live position when no row exists.
 */
export async function cancelUntrackedOrder(
    authedApi: KyInstance,
    body: CancelUntrackedOrderRequest,
): Promise<CancelUntrackedOrderResponse> {
    const data = await authedApi
        .post('speed/trade/orders/cancel', { json: body })
        .json();
    return cancelUntrackedOrderResponseSchema.parse(data);
}
